import { useState, useEffect } from "react";
import { FaTrash, FaEye, FaEdit, FaPlus, FaSearch, FaImage, FaFilm, FaTimes, FaUpload, FaSpinner, FaPlay, FaPause, FaChevronLeft, FaChevronRight } from "react-icons/fa";
import { utils, writeFile } from "xlsx";
import axios from "axios";

const BannerList = () => {
  const [banners, setBanners] = useState([]);
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedBanner, setSelectedBanner] = useState(null);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [editingBanner, setEditingBanner] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editFormData, setEditFormData] = useState({
    title: "",
    subtitle: "",
    type: "images",
    media: []
  });
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [filePreviews, setFilePreviews] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [playingVideo, setPlayingVideo] = useState(null);
  
  const bannersPerPage = 8;

  useEffect(() => {
    fetchBanners();
  }, []);

  // Cleanup previews on unmount
  useEffect(() => {
    return () => {
      filePreviews.forEach(preview => URL.revokeObjectURL(preview));
    };
  }, [filePreviews]);

  // Get all banners API
  const fetchBanners = async () => {
    try {
      setLoading(true);
      const response = await axios.get("https://api.dreamtoday.in/api/admin/allbanner");
      
      if (response.data && response.data.success) {
        setBanners(response.data.banners);
      }
    } catch (error) {
      console.error("Error fetching banners:", error);
      alert("Failed to fetch banners");
    } finally {
      setLoading(false);
    }
  };

  // Delete banner API
  const handleDelete = async (bannerId) => {
    if (window.confirm("Are you sure you want to delete this banner?")) {
      try {
        const response = await axios.delete(`https://api.dreamtoday.in/api/admin/deletebanner/${bannerId}`);
        
        if (response.data && response.data.success) {
          alert("Banner deleted successfully");
          setBanners(banners.filter((banner) => banner._id !== bannerId));
        } else {
          alert(response.data.message || "Failed to delete banner");
        }
      } catch (error) {
        console.error("Error deleting banner:", error);
        alert(error.response?.data?.message || "Failed to delete banner");
      }
    }
  };

  // Open edit modal with banner data
  const openEditModal = (banner) => {
    setEditingBanner(banner);
    setEditFormData({
      title: banner.title || "",
      subtitle: banner.subtitle || "",
      type: banner.type || "images",
      media: banner.media || []
    });
    setSelectedFiles([]);
    setFilePreviews([]);
    setIsEditModalOpen(true);
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEditFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle file selection for edit
  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    
    // Validate based on type
    if (editFormData.type === "video") {
      if (files.length > 1) {
        alert("Only one video file can be uploaded");
        return;
      }
      const file = files[0];
      if (!file.type.startsWith("video/")) {
        alert("Please select a valid video file");
        return;
      }
      if (file.size > 100 * 1024 * 1024) { // 100MB limit
        alert("Video file size should be less than 100MB");
        return;
      }
    } else {
      // Images validation
      const invalidFiles = files.filter(file => !file.type.startsWith("image/"));
      if (invalidFiles.length > 0) {
        alert("Please select only image files");
        return;
      }
      const oversizedFiles = files.filter(file => file.size > 5 * 1024 * 1024); // 5MB limit
      if (oversizedFiles.length > 0) {
        alert("Each image should be less than 5MB");
        return;
      }
    }

    // Clear old previews
    filePreviews.forEach(preview => URL.revokeObjectURL(preview));
    
    // Create new previews
    const newPreviews = files.map(file => URL.createObjectURL(file));
    
    setSelectedFiles(files);
    setFilePreviews(newPreviews);
  };

  // Remove selected file
  const removeSelectedFile = (index) => {
    URL.revokeObjectURL(filePreviews[index]);
    
    const newFiles = selectedFiles.filter((_, i) => i !== index);
    const newPreviews = filePreviews.filter((_, i) => i !== index);
    
    setSelectedFiles(newFiles);
    setFilePreviews(newPreviews);
  };

  // Remove existing media
  const removeExistingMedia = (index) => {
    setEditFormData(prev => ({
      ...prev,
      media: prev.media.filter((_, i) => i !== index)
    }));
  };

  // Update banner API
  const handleUpdate = async (e) => {
    e.preventDefault();
    
    try {
      setUploading(true);
      
      // Create form data for multipart upload
      const formData = new FormData();
      
      // Append basic fields
      formData.append("title", editFormData.title);
      formData.append("subtitle", editFormData.subtitle || "");
      formData.append("type", editFormData.type);
      
      // Append existing media paths
      formData.append("existingMedia", JSON.stringify(editFormData.media));
      
      // Append new files
      selectedFiles.forEach(file => {
        if (editFormData.type === "video") {
          formData.append("file", file);
        } else {
          formData.append("files", file);
        }
      });

      const response = await axios.put(
        `https://api.dreamtoday.in/api/admin/updatebanner/${editingBanner._id}`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data"
          }
        }
      );
      
      if (response.data && response.data.success) {
        alert("Banner updated successfully");
        fetchBanners(); // Refresh the list
        setIsEditModalOpen(false);
        setEditingBanner(null);
        setSelectedFiles([]);
        setFilePreviews([]);
      } else {
        alert(response.data.message || "Failed to update banner");
      }
    } catch (error) {
      console.error("Error updating banner:", error);
      alert(error.response?.data?.message || "Failed to update banner");
    } finally {
      setUploading(false);
    }
  };

  const exportData = (type) => {
    const exportBanners = filteredBanners.map((banner) => ({
      ID: banner._id,
      Title: banner.title || "N/A",
      Subtitle: banner.subtitle || "N/A",
      Type: banner.type || "N/A",
      MediaCount: banner.media?.length || 0,
      CreatedAt: new Date(banner.createdAt).toLocaleDateString(),
    }));
    
    const ws = utils.json_to_sheet(exportBanners);
    const wb = utils.book_new();
    utils.book_append_sheet(wb, ws, "Banners");
    writeFile(wb, `banners_${new Date().toISOString().split('T')[0]}.${type}`);
  };

  const openViewModal = (banner) => {
    setSelectedBanner(banner);
    setIsViewModalOpen(true);
  };

  const getTypeIcon = (type) => {
    return type === "video" ? <FaFilm className="text-purple-500" /> : <FaImage className="text-blue-500" />;
  };

  const filteredBanners = banners.filter((banner) =>
    (banner.title || "").toLowerCase().includes(search.toLowerCase()) ||
    (banner.subtitle || "").toLowerCase().includes(search.toLowerCase())
  );

  const indexOfLastBanner = currentPage * bannersPerPage;
  const indexOfFirstBanner = indexOfLastBanner - bannersPerPage;
  const currentBanners = filteredBanners.slice(indexOfFirstBanner, indexOfLastBanner);
  const totalPages = Math.ceil(filteredBanners.length / bannersPerPage);

  const toggleVideoPlay = (index) => {
    if (playingVideo === index) {
      setPlayingVideo(null);
    } else {
      setPlayingVideo(index);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading banners...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Banner Management</h1>
        <p className="text-gray-600">Manage all banners in the system</p>
      </div>
      
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
        <div className="bg-white rounded-xl shadow p-6 border border-gray-200">
          <div className="flex items-center">
            <div className="p-3 bg-blue-100 rounded-lg mr-4">
              <FaImage className="text-blue-600 text-xl" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Total Banners</p>
              <p className="text-2xl font-bold text-gray-900">{banners.length}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-xl shadow p-6 border border-gray-200">
          <div className="flex items-center">
            <div className="p-3 bg-green-100 rounded-lg mr-4">
              <FaImage className="text-green-600 text-xl" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Image Banners</p>
              <p className="text-2xl font-bold text-gray-900">
                {banners.filter(b => b.type === "images").length}
              </p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-xl shadow p-6 border border-gray-200">
          <div className="flex items-center">
            <div className="p-3 bg-purple-100 rounded-lg mr-4">
              <FaFilm className="text-purple-600 text-xl" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Video Banners</p>
              <p className="text-2xl font-bold text-gray-900">
                {banners.filter(b => b.type === "video").length}
              </p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-xl shadow p-6 border border-gray-200">
          <div className="flex items-center">
            <div className="p-3 bg-amber-100 rounded-lg mr-4">
              <FaEye className="text-amber-600 text-xl" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Total Media</p>
              <p className="text-2xl font-bold text-gray-900">
                {banners.reduce((sum, b) => sum + (b.media?.length || 0), 0)}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Search and Export */}
      <div className="bg-white rounded-xl shadow border border-gray-200 p-6 mb-6">
        <div className="flex flex-col md:flex-row gap-4 md:items-center justify-between">
          <div className="relative flex-1">
            <FaSearch className="absolute left-4 top-3.5 text-gray-400" />
            <input
              type="text"
              placeholder="Search banners by title or subtitle..."
              className="w-full px-4 py-3 pl-12 bg-gray-50 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          
          <div className="flex gap-3">
            <button
              onClick={() => exportData("csv")}
              className="px-4 py-3 bg-green-500 text-white rounded-lg hover:bg-green-600 transition flex items-center gap-2 font-medium"
            >
              <FaUpload />
              CSV
            </button>
            <button
              onClick={() => exportData("xlsx")}
              className="px-4 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition flex items-center gap-2 font-medium"
            >
              <FaUpload />
              Excel
            </button>
          </div>
        </div>
      </div>

      {/* Banners Table */}
      <div className="bg-white rounded-xl shadow border border-gray-200 overflow-hidden mb-6">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 border-b">
                <th className="py-4 px-6 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  Banner Details
                </th>
                <th className="py-4 px-6 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  Type
                </th>
                <th className="py-4 px-6 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  Media
                </th>
                <th className="py-4 px-6 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  Created
                </th>
                <th className="py-4 px-6 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {currentBanners.map((banner) => (
                <tr key={banner._id} className="hover:bg-gray-50 transition-colors">
                  <td className="py-4 px-6">
                    <div className="flex items-center">
                      {banner.type === "video" ? (
                        <div className="h-10 w-10 bg-purple-100 rounded-lg flex items-center justify-center">
                          <FaFilm className="text-purple-600" />
                        </div>
                      ) : (
                        <div className="h-10 w-10 bg-blue-100 rounded-lg flex items-center justify-center">
                          <FaImage className="text-blue-600" />
                        </div>
                      )}
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">
                          {banner.title}
                        </div>
                        {banner.subtitle && (
                          <div className="text-xs text-gray-500">
                            {banner.subtitle}
                          </div>
                        )}
                      </div>
                    </div>
                  </td>
                  
                  <td className="py-4 px-6">
                    <span className={`inline-flex items-center px-3 py-1.5 rounded-full text-xs font-semibold ${
                      banner.type === "video" 
                        ? "text-purple-600 bg-purple-50 border border-purple-200"
                        : "text-blue-600 bg-blue-50 border border-blue-200"
                    }`}>
                      {getTypeIcon(banner.type)}
                      <span className="ml-1">{banner.type?.toUpperCase()}</span>
                    </span>
                  </td>
                  
                  <td className="py-4 px-6">
                    <div className="flex -space-x-2">
                      {banner.media?.slice(0, 3).map((media, idx) => (
                        <div
                          key={idx}
                          className="w-8 h-8 rounded-full bg-gradient-to-br from-gray-100 to-gray-200 border-2 border-white flex items-center justify-center overflow-hidden"
                          title={media.split('/').pop()}
                        >
                          {banner.type === "video" ? (
                            <FaFilm className="text-purple-600 text-xs" />
                          ) : (
                            <img 
                              src={`https://api.dreamtoday.in${media}`}
                              alt={`Media ${idx + 1}`}
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = "https://via.placeholder.com/32";
                              }}
                            />
                          )}
                        </div>
                      ))}
                      {banner.media?.length > 3 && (
                        <div className="w-8 h-8 rounded-full bg-gray-100 border-2 border-white flex items-center justify-center text-xs font-medium text-gray-600">
                          +{banner.media.length - 3}
                        </div>
                      )}
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      {banner.media?.length || 0} file(s)
                    </div>
                  </td>
                  
                  <td className="py-4 px-6">
                    <div className="text-sm text-gray-600">
                      {new Date(banner.createdAt).toLocaleDateString()}
                    </div>
                  </td>
                  
                  <td className="py-4 px-6">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => openViewModal(banner)}
                        className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition border border-blue-200 hover:border-blue-300"
                        title="View Details"
                      >
                        <FaEye />
                      </button>
                      <button
                        onClick={() => openEditModal(banner)}
                        className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition border border-green-200 hover:border-green-300"
                        title="Edit Banner"
                      >
                        <FaEdit />
                      </button>
                      <button
                        onClick={() => handleDelete(banner._id)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition border border-red-200 hover:border-red-300"
                        title="Delete Banner"
                      >
                        <FaTrash />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Empty State */}
        {currentBanners.length === 0 && (
          <div className="text-center py-12">
            <div className="text-gray-400 mb-4">
              {search ? <FaSearch className="h-16 w-16 mx-auto" /> : <FaImage className="h-16 w-16 mx-auto" />}
            </div>
            <p className="text-gray-500 text-lg font-medium">
              {search ? "No banners found" : "No banners yet"}
            </p>
            <p className="text-gray-400 mt-2">
              {search ? "Try adjusting your search criteria" : "Create your first banner to get started"}
            </p>
          </div>
        )}
      </div>

      {/* Pagination */}
      {filteredBanners.length > 0 && (
        <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
          <div className="text-sm text-gray-600">
            Showing <span className="font-semibold">{indexOfFirstBanner + 1}</span> to{" "}
            <span className="font-semibold">{Math.min(indexOfLastBanner, filteredBanners.length)}</span> of{" "}
            <span className="font-semibold">{filteredBanners.length}</span> banners
          </div>
          
          <div className="flex items-center gap-2">
            <button
              onClick={() => setCurrentPage(Math.max(currentPage - 1, 1))}
              disabled={currentPage === 1}
              className="px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition flex items-center gap-2"
            >
              <FaChevronLeft size={12} /> Previous
            </button>
            
            <div className="flex items-center gap-1">
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNumber;
                if (totalPages <= 5) {
                  pageNumber = i + 1;
                } else if (currentPage <= 3) {
                  pageNumber = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNumber = totalPages - 4 + i;
                } else {
                  pageNumber = currentPage - 2 + i;
                }
                
                return (
                  <button
                    key={pageNumber}
                    onClick={() => setCurrentPage(pageNumber)}
                    className={`w-10 h-10 rounded-lg transition ${
                      currentPage === pageNumber
                        ? "bg-blue-600 text-white"
                        : "bg-white border border-gray-300 text-gray-700 hover:bg-gray-50"
                    }`}
                  >
                    {pageNumber}
                  </button>
                );
              })}
            </div>
            
            <button
              onClick={() => setCurrentPage(Math.min(currentPage + 1, totalPages))}
              disabled={currentPage === totalPages}
              className="px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition flex items-center gap-2"
            >
              Next <FaChevronRight size={12} />
            </button>
          </div>
        </div>
      )}

      {/* View Banner Modal */}
      {isViewModalOpen && selectedBanner && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" onClick={() => setIsViewModalOpen(false)}></div>
          
          <div className="flex min-h-screen items-center justify-center p-4">
            <div className="relative bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
              {/* Sticky Header */}
              <div className="sticky top-0 bg-white z-10 p-6 pb-4 border-b flex justify-between items-center">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">Banner Details</h2>
                  <p className="text-gray-600">Complete information about the banner</p>
                </div>
                <button
                  onClick={() => setIsViewModalOpen(false)}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <FaTimes className="text-gray-500 hover:text-gray-700" size={20} />
                </button>
              </div>

              {/* Scrollable Content */}
              <div className="p-6 pt-2">
                <div className="space-y-6">
                  {/* Banner Media */}
                  {selectedBanner.media && selectedBanner.media.length > 0 && (
                    <div className="bg-gradient-to-r from-blue-50 to-cyan-50 rounded-xl p-5">
                      <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                        {selectedBanner.type === "video" ? (
                          <FaFilm className="mr-2 text-purple-600" />
                        ) : (
                          <FaImage className="mr-2 text-blue-600" />
                        )}
                        {selectedBanner.type === "video" ? "Video" : "Images"}
                      </h3>
                      
                      {selectedBanner.type === "video" ? (
                        <div className="aspect-video bg-gray-900 rounded-lg overflow-hidden">
                          <video
                            src={`https://api.dreamtoday.in${selectedBanner.media[0]}`}
                            controls
                            className="w-full h-full"
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.parentElement.innerHTML = '<div class="w-full h-full flex items-center justify-center text-white">Video failed to load</div>';
                            }}
                          />
                        </div>
                      ) : (
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                          {selectedBanner.media.map((media, index) => (
                            <div key={index} className="relative group">
                              <img
                                src={`https://api.dreamtoday.in${media}`}
                                alt={`Banner ${index + 1}`}
                                className="w-full h-40 object-cover rounded-lg shadow-md group-hover:shadow-xl transition"
                                onError={(e) => {
                                  e.target.onerror = null;
                                  e.target.src = "https://via.placeholder.com/150?text=Image+Error";
                                }}
                              />
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Basic Info */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="bg-white p-5 rounded-xl shadow-sm border border-gray-100">
                      <h3 className="font-semibold text-gray-700 mb-3">Banner Information</h3>
                      <div className="space-y-2">
                        <p><span className="text-gray-500">Title:</span> {selectedBanner.title}</p>
                        {selectedBanner.subtitle && (
                          <p><span className="text-gray-500">Subtitle:</span> {selectedBanner.subtitle}</p>
                        )}
                        <p>
                          <span className="text-gray-500">Type:</span>{" "}
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold ${
                            selectedBanner.type === "video" 
                              ? "text-purple-600 bg-purple-50"
                              : "text-blue-600 bg-blue-50"
                          }`}>
                            {getTypeIcon(selectedBanner.type)}
                            <span className="ml-1">{selectedBanner.type?.toUpperCase()}</span>
                          </span>
                        </p>
                      </div>
                    </div>

                    <div className="bg-white p-5 rounded-xl shadow-sm border border-gray-100">
                      <h3 className="font-semibold text-gray-700 mb-3">Date Information</h3>
                      <div className="space-y-2">
                        <p><span className="text-gray-500">Created:</span> {new Date(selectedBanner.createdAt).toLocaleString()}</p>
                        {selectedBanner.updatedAt && (
                          <p><span className="text-gray-500">Updated:</span> {new Date(selectedBanner.updatedAt).toLocaleString()}</p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Media Info */}
                  <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl p-5">
                    <h3 className="text-lg font-bold text-gray-900 mb-4">Media Information</h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="bg-white p-4 rounded-lg shadow-sm">
                        <p className="text-sm text-gray-500">Total Files</p>
                        <p className="text-2xl font-bold text-gray-900">
                          {selectedBanner.media?.length || 0}
                        </p>
                      </div>
                      <div className="bg-white p-4 rounded-lg shadow-sm">
                        <p className="text-sm text-gray-500">Banner Type</p>
                        <p className="text-xl font-semibold text-gray-900 capitalize">
                          {selectedBanner.type}
                        </p>
                      </div>
                      <div className="bg-white p-4 rounded-lg shadow-sm">
                        <p className="text-sm text-gray-500">Banner ID</p>
                        <p className="text-xs font-mono text-gray-900 break-all">
                          {selectedBanner._id}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Sticky Footer */}
              <div className="sticky bottom-0 bg-white border-t p-6">
                <div className="flex gap-3">
                  <button
                    onClick={() => {
                      setIsViewModalOpen(false);
                      openEditModal(selectedBanner);
                    }}
                    className="flex-1 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition font-medium"
                  >
                    Edit Banner
                  </button>
                  <button
                    onClick={() => setIsViewModalOpen(false)}
                    className="flex-1 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition font-medium"
                  >
                    Close Details
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Banner Modal */}
      {isEditModalOpen && editingBanner && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" onClick={() => {
            setIsEditModalOpen(false);
            setSelectedFiles([]);
            setFilePreviews([]);
          }}></div>
          
          <div className="flex min-h-screen items-center justify-center p-4">
            <div className="relative bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
              {/* Sticky Header */}
              <div className="sticky top-0 bg-white z-10 p-6 pb-4 border-b flex justify-between items-center">
                <h2 className="text-2xl font-bold text-gray-900">Edit Banner</h2>
                <button
                  onClick={() => {
                    setIsEditModalOpen(false);
                    setSelectedFiles([]);
                    setFilePreviews([]);
                  }}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <FaTimes className="text-gray-500 hover:text-gray-700" size={20} />
                </button>
              </div>

              {/* Scrollable Form */}
              <div className="p-6 pt-2">
                <form onSubmit={handleUpdate} className="space-y-6">
                  {/* Title */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Banner Title *
                    </label>
                    <input
                      type="text"
                      name="title"
                      value={editFormData.title}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                      required
                    />
                  </div>

                  {/* Subtitle */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Subtitle
                    </label>
                    <input
                      type="text"
                      name="subtitle"
                      value={editFormData.subtitle}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                    />
                  </div>

                  {/* Banner Type - Readonly in edit */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Banner Type
                    </label>
                    <div className={`px-4 py-3 bg-gray-50 border border-gray-300 rounded-lg text-gray-700 flex items-center gap-2`}>
                      {editFormData.type === "video" ? <FaFilm className="text-purple-500" /> : <FaImage className="text-blue-500" />}
                      <span className="capitalize">{editFormData.type} Banner</span>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">Type cannot be changed after creation</p>
                  </div>

                  {/* Existing Media */}
                  {editFormData.media.length > 0 && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Existing {editFormData.type === "video" ? "Video" : "Images"}
                      </label>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        {editFormData.media.map((media, index) => (
                          <div key={index} className="relative group">
                            {editFormData.type === "video" ? (
                              <div className="aspect-video bg-gray-900 rounded-lg overflow-hidden">
                                <video
                                  src={`https://api.dreamtoday.in${media}`}
                                  className="w-full h-full object-contain"
                                  controls
                                />
                              </div>
                            ) : (
                              <img
                                src={`https://api.dreamtoday.in${media}`}
                                alt={`Media ${index + 1}`}
                                className="w-full h-24 object-cover rounded-lg"
                                onError={(e) => {
                                  e.target.onerror = null;
                                  e.target.src = "https://via.placeholder.com/100";
                                }}
                              />
                            )}
                            <button
                              type="button"
                              onClick={() => removeExistingMedia(index)}
                              className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition"
                              title="Remove this media"
                            >
                              <FaTimes size={12} />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* New Media Upload */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {editFormData.type === "video" ? "Replace Video (Optional)" : "Add More Images (Optional)"}
                    </label>
                    
                    <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-lg hover:border-blue-500 transition-colors">
                      <div className="space-y-1 text-center">
                        {editFormData.type === "video" ? (
                          <FaFilm className="mx-auto h-12 w-12 text-gray-400" />
                        ) : (
                          <FaImage className="mx-auto h-12 w-12 text-gray-400" />
                        )}
                        
                        <div className="flex text-sm text-gray-600">
                          <label
                            htmlFor="edit-file-upload"
                            className="relative cursor-pointer bg-white rounded-md font-medium text-blue-600 hover:text-blue-500 focus-within:outline-none"
                          >
                            <span>Upload new files</span>
                            <input
                              id="edit-file-upload"
                              name="file-upload"
                              type="file"
                              className="sr-only"
                              accept={editFormData.type === "video" ? "video/*" : "image/*"}
                              multiple={editFormData.type === "images"}
                              onChange={handleFileSelect}
                            />
                          </label>
                          <p className="pl-1">or drag and drop</p>
                        </div>
                        <p className="text-xs text-gray-500">
                          {editFormData.type === "video" 
                            ? "MP4, WebM, Ogg up to 100MB"
                            : "PNG, JPG, GIF up to 5MB each"
                          }
                        </p>
                      </div>
                    </div>

                    {/* New Files Preview */}
                    {filePreviews.length > 0 && (
                      <div className="mt-4">
                        <p className="text-sm text-gray-600 mb-2">New Files Preview:</p>
                        <div className={`grid gap-4 ${
                          editFormData.type === "video" 
                            ? "grid-cols-1" 
                            : "grid-cols-2 md:grid-cols-4"
                        }`}>
                          {filePreviews.map((preview, index) => (
                            <div key={index} className="relative group">
                              {editFormData.type === "video" ? (
                                <div className="aspect-video bg-gray-900 rounded-lg overflow-hidden">
                                  <video
                                    src={preview}
                                    controls
                                    className="w-full h-full object-contain"
                                  />
                                </div>
                              ) : (
                                <img
                                  src={preview}
                                  alt={`New ${index + 1}`}
                                  className="w-full h-24 object-cover rounded-lg"
                                />
                              )}
                              <button
                                type="button"
                                onClick={() => removeSelectedFile(index)}
                                className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition"
                              >
                                <FaTimes size={12} />
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </form>
              </div>

              {/* Sticky Footer */}
              <div className="sticky bottom-0 bg-white border-t p-6">
                <div className="flex gap-3">
                  <button
                    type="submit"
                    onClick={handleUpdate}
                    disabled={uploading}
                    className="flex-1 bg-blue-600 text-white py-3 rounded-lg hover:bg-blue-700 transition font-medium disabled:bg-blue-400 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {uploading ? (
                      <>
                        <FaSpinner className="animate-spin" />
                        Updating...
                      </>
                    ) : (
                      <>
                        <FaUpload />
                        Update Banner
                      </>
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setIsEditModalOpen(false);
                      setSelectedFiles([]);
                      setFilePreviews([]);
                    }}
                    className="flex-1 bg-gray-100 text-gray-700 py-3 rounded-lg hover:bg-gray-200 transition font-medium"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BannerList;